"use client";

import * as React from "react";
import { X, Plus, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger, } from "@/components/ui/popover";
import { Command, CommandInput, CommandItem, CommandGroup, CommandEmpty, CommandList, } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
interface MultiSelectProps {
    label?: string;
    options: string[];
    value: string[]; // always array for consistency
    onChange: (value: string[]) => void;
    placeholder?: string;
    mode?: "single" | "multiple"; // NEW
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
    label = "Add item",
    options,
    value,
    onChange,
    placeholder = "Search or type...",
    mode = "multiple",
}) => {
    const [open, setOpen] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState("");

    const handleAdd = (item: string) => {
        if (mode === "single") {
            onChange([item]); // only one allowed
            setOpen(false);
        } else {
            if (!value.includes(item)) {
                onChange([...value, item]);
            }
        }
        setSearchValue("");
    };

    const handleRemove = (item: string) => {
        onChange(value.filter((v) => v !== item));
    };

    const filteredOptions = options.filter(
        (opt) =>
            opt.toLowerCase().includes(searchValue.toLowerCase()) &&
            !value.includes(opt)
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && searchValue.trim()) {
            e.preventDefault();
            handleAdd(searchValue.trim());
        }
    };

    return (
        <div className="flex flex-col gap-2 justify-center">
            <Label className="text-sm font-medium">
                {label}
            </Label>

            {/* Trigger */}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between h-10"
                    >
                        <div className="flex items-center gap-1">
                            <Plus size={12} />
                            <span className="text-sm">{label}</span>
                        </div>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0 bg-white z-50" align="start">
                    <Command>
                        <CommandInput
                            value={searchValue}
                            placeholder={placeholder}
                            onValueChange={setSearchValue}
                            onKeyDown={handleKeyDown}
                        />
                        <CommandList>
                            <CommandEmpty>
                                {searchValue ? (
                                    <button
                                        type="button"
                                        className="w-full text-left px-2 py-1 text-sm text-blue-600"
                                        onClick={() => handleAdd(searchValue)}
                                    >
                                        Add "{searchValue}"
                                    </button>
                                ) : (
                                    "No results."
                                )}
                            </CommandEmpty>
                            <CommandGroup>
                                {filteredOptions.map((opt) => (
                                    <CommandItem key={opt} onSelect={() => handleAdd(opt)}>
                                        {opt}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            {/* Single mode selected display */}
            {mode === "single" && value[0] && (
                <div className="mt-1 text-sm flex items-center gap-1">
                    <span className="px-2 py-0.5 rounded-full bg-gray-100">{value[0]}</span>
                    <button
                        type="button"
                        onClick={() => onChange([])}
                        className="hover:text-red-500"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}

            {/* Selected Tags (only for multi mode) */}
            {mode === "multiple" && (
                <div className="flex flex-wrap gap-1">
                    {value.map((val) => (
                        <span
                            key={val}
                            className="flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-sm"
                        >
                            {val}
                            <button
                                type="button"
                                onClick={() => handleRemove(val)}
                                className="hover:text-red-500"
                            >
                                <X size={14} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};
